import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useEspectroAccount } from "../hooks/useEspectroAccount.ts";
import type { UTXO } from "../zcash/lightwalletd.ts";
import { EXIT_ASSETS, type AssetPair } from "../swapkit/helpers.ts";
import {
  type EspectroOperation,
  type EnterEvidence,
  type ExitEvidence,
  type TransferEvidence,
  STAGE_LABELS,
  TRANSFER_STAGE_LABELS,
  isTerminalStage,
  getResumeAction,
  hasChainCommitment,
  type ResumeAction,
  type TransferResumeAction,
} from "../lit/operation.ts";

const RECOVER_STEP_LABELS: Record<string, string> = {
  checking: "Checking balances...",
  scanning: "Scanning Orchard pool...",
  building: "Building transaction...",
  building_tx: "Building swap transaction...",
  broadcasting_unshield: "Broadcasting unshield...",
  confirming: "Waiting for unshield confirmation...",
  quoting: "Getting swap quote...",
  signing: "Creating swap order...",
  broadcasting: "Broadcasting to network...",
  done: "Done",
};

const RECOVER_STEP_TIMES: Record<string, string> = {
  checking: "~5s",
  scanning: "~8m",
  building: "~30s",
  building_tx: "~10s",
  broadcasting_unshield: "~10s",
  confirming: "~2m",
  quoting: "~10s",
  signing: "~10s",
  broadcasting: "~10s",
};

const RECOVER_STEP_SECONDS: Record<string, number> = {
  checking: 5,
  scanning: 480,
  building: 30,
  building_tx: 10,
  broadcasting_unshield: 10,
  confirming: 120,
  quoting: 10,
  signing: 10,
  broadcasting: 10,
};

const RESUME_ACTION_LABELS: Record<ResumeAction, string> = {
  retry_broadcast_shield: "Retry Broadcast (Shield)",
  retry_broadcast_unshield: "Retry Broadcast (Unshield)",
  wait_inbound: "Waiting for ZEC deposit...",
  shield_stuck_funds: "Shield Stuck Funds",
  wait_shield_confirm: "Waiting for shield confirmation...",
  wait_swap: "Waiting for swap...",
  resume_exit_swap_tracking: "Resume Swap Tracking",
  none: "No action needed",
};

const RESUME_ACTION_DESCRIPTIONS: Record<ResumeAction, string> = {
  retry_broadcast_shield:
    "The shielding transaction was built but may not have been broadcast. Retry sending it to the Zcash network.",
  retry_broadcast_unshield:
    "The unshielding transaction was built but may not have been broadcast. Retry sending it to the Zcash network.",
  wait_inbound:
    "The source swap was initiated. Waiting for ZEC to arrive at the transparent address. You can also check for stuck funds below.",
  shield_stuck_funds:
    "ZEC was detected at the transparent address. Shield it into the Orchard pool to continue.",
  wait_shield_confirm:
    "The shielding transaction was broadcast. Waiting for it to confirm on-chain. The balance will update automatically.",
  wait_swap:
    "The exit unshielding was broadcast. Waiting for the swap to complete.",
  resume_exit_swap_tracking:
    "The exit swap was started but tracking was interrupted. Resume tracking to check the swap status.",
  none: "This operation cannot be automatically resumed. You may need to start a new operation.",
};

export function RecoveryPage() {
  const navigate = useNavigate();
  const espectro = useEspectroAccount();

  const [transparentUtxos, setTransparentUtxos] = useState<UTXO[] | null>(
    null,
  );
  const [transparentTotal, setTransparentTotal] = useState(0);
  const [, setCheckLoading] = useState(false);
  const [, setCheckError] = useState<string | null>(null);

  const [recoverDestAddress, setRecoverDestAddress] = useState("");
  const [recoverSelectedAsset, setRecoverSelectedAsset] = useState<AssetPair>(EXIT_ASSETS[0]);
  const [txHashInput, setTxHashInput] = useState("");

  const [broadcastResult, setBroadcastResult] = useState<string | null>(null);
  const [broadcastError, setBroadcastError] = useState<string | null>(null);
  const [broadcastBusy, setBroadcastBusy] = useState(false);

  const [trackingBusy, setTrackingBusy] = useState(false);
  const [trackingStatus, setTrackingStatus] = useState<string | null>(null);

  const [stepElapsed, setStepElapsed] = useState(0); // seconds
  const stepStartRef = useRef<number | null>(null);
  const recoveryStartRef = useRef<number | null>(null);
  const [totalElapsed, setTotalElapsed] = useState(0);

  useEffect(() => {
    if (espectro.recoverStep && espectro.recoverStep !== "done") {
      if (!recoveryStartRef.current) {
        recoveryStartRef.current = Date.now();
      }
      stepStartRef.current = Date.now();
      setStepElapsed(0);
      const id = setInterval(() => {
        setStepElapsed(Math.floor((Date.now() - (stepStartRef.current ?? Date.now())) / 1000));
      }, 1000);
      return () => clearInterval(id);
    }
    if (espectro.recoverStep === "done" && recoveryStartRef.current) {
      setTotalElapsed(Math.floor((Date.now() - recoveryStartRef.current) / 1000));
    }
  }, [espectro.recoverStep]);

  // No redirect — Recovery page is always accessible so users can re-derive
  // their seed after clearing localStorage and recover stuck funds.

  // Run reconciliation on mount
  useEffect(() => {
    if (espectro.account) {
      espectro.reconcile();
    }
  }, [espectro.account, espectro.reconcile]);

  const checkTransparent = useCallback(async () => {
    setCheckLoading(true);
    setCheckError(null);
    try {
      const { utxos, totalZec } = await espectro.getTransparentBalance();
      setTransparentUtxos(utxos);
      setTransparentTotal(totalZec);
    } catch (err: unknown) {
      setCheckError(
        err instanceof Error ? err.message : "Failed to check balance",
      );
    } finally {
      setCheckLoading(false);
    }
  }, [espectro.getTransparentBalance]);

  useEffect(() => {
    if (espectro.account) {
      checkTransparent();
      espectro.scanOrchard();
    }
  }, [espectro.account, checkTransparent, espectro.scanOrchard]);

  const op: EspectroOperation | null = espectro.operation;
  const resumeAction: ResumeAction | TransferResumeAction = op ? getResumeAction(op) : "none";
  const hasStuckFunds =
    transparentUtxos !== null && transparentUtxos.length > 0;

  function getOriginFromOperation(): { asset: string; address: string } | null {
    if (!op) return null;
    if (op.type === "enter") {
      const ev = op.evidence as EnterEvidence;
      if (ev.sourceAsset && ev.sourceAddress) {
        return { asset: ev.sourceAsset, address: ev.sourceAddress };
      }
    }
    if (op.type === "transfer") {
      const ev = op.evidence as TransferEvidence;
      if (ev.sourceAsset && ev.sourceAddress) {
        return { asset: ev.sourceAsset, address: ev.sourceAddress };
      }
    }
    return null;
  }

  getOriginFromOperation();

  async function handleRetryBroadcast() {
    if (!op) return;
    const ev = op.evidence as EnterEvidence & ExitEvidence;
    const rawTx = ev.shieldRawTx ?? ev.unshieldRawTx;
    if (!rawTx) return;

    setBroadcastBusy(true);
    setBroadcastError(null);
    setBroadcastResult(null);
    try {
      const hash = await espectro.retryBroadcast(rawTx);
      setBroadcastResult(hash);
      checkTransparent();
    } catch (err: unknown) {
      setBroadcastError(
        err instanceof Error ? err.message : "Broadcast failed",
      );
    } finally {
      setBroadcastBusy(false);
    }
  }

  async function handleResumeSwapTracking() {
    setTrackingBusy(true);
    setTrackingStatus(null);
    try {
      await espectro.resumeExitSwapTracking(setTrackingStatus);
    } catch (err: unknown) {
      setTrackingStatus(
        `Error: ${err instanceof Error ? err.message : "Tracking failed"}`,
      );
    } finally {
      setTrackingBusy(false);
    }
  }

  function handleDismissOperation() {
    espectro.dismissOperation();
  }

  if (espectro.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-mist">Loading...</p>
      </div>
    );
  }

  // No seed in memory — prompt wallet signature to re-derive Zcash keys.
  // This happens on fresh page load (seed not persisted) or after localStorage clear.
  if (!espectro.account || espectro.needsReauth) {
    return (
      <div className="min-h-screen flex items-start justify-center p-4 md:p-8">
        <div className="w-full max-w-140 mt-4">
          <div className="flex items-center gap-3 mb-8">
            <button onClick={() => navigate("/app")} className="text-ash hover:text-mist transition-colors" aria-label="Back">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span className="font-display text-2xl spectral-gradient-text">Recovery</span>
          </div>

          <div className="rounded-2xl p-6 border border-white/8" style={{ background: "#122134" }}>
            <p className="font-heading text-ghost-white font-semibold mb-2">Sign to recover your funds</p>
            <p className="text-sm text-ash leading-relaxed mb-6">
              Your Zcash keys are derived from your wallet signature — they are not stored anywhere.
              Sign with the same wallet you used for the transfer to restore access to your funds.
            </p>
            {espectro.error && (
              <p className="text-sm text-amber-400 mb-4">{espectro.error}</p>
            )}
            <button
              onClick={() => espectro.createAccount()}
              disabled={espectro.isCreating}
              className="w-full py-3 rounded-xl text-white font-heading text-sm font-semibold transition-all disabled:opacity-50"
              style={{ background: "#3b83da" }}
            >
              {espectro.isCreating ? "Waiting for signature..." : "Sign with Wallet"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const acct = espectro.account;


  return (
    <div className="min-h-screen flex items-start justify-center p-4 md:p-8">
      <div className="w-full max-w-140 mt-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/app")}
              className="text-ash hover:text-mist transition-colors"
              aria-label="Back"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M12.5 15L7.5 10L12.5 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <span className="font-display text-2xl spectral-gradient-text">
              Recovery
            </span>
          </div>
        </div>

        {/* Active Operation */}
        {op && !isTerminalStage(op.stage) && (
          <div className="rounded-2xl p-6 mb-4" style={{ background: "#122134" }}>
            <div className="font-heading text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-ash mb-4">
              Active Operation
            </div>

            <div className="space-y-2 mb-4">
              <Field
                label="Type"
                value={
                  op.type === "transfer"
                    ? "Private Transfer"
                    : op.type === "enter"
                      ? "Enter (Shield)"
                      : "Exit (Unshield)"
                }
              />
              <Field
                label="Stage"
                value={
                  op.type === "transfer"
                    ? (TRANSFER_STAGE_LABELS as Record<string, string>)[op.stage] ?? op.stage
                    : STAGE_LABELS[op.stage] ?? op.stage
                }
              />
              <Field
                label="Started"
                value={new Date(op.createdAt).toLocaleString()}
              />
              {op.type === "transfer" && (
                <>
                  {(op.evidence as TransferEvidence).enterSwapId && (
                    <Field
                      label="Enter Swap"
                      value={truncate((op.evidence as TransferEvidence).enterSwapId!, 16)}
                    />
                  )}
                  {(op.evidence as TransferEvidence).shieldTxId && (
                    <Field
                      label="Shield Tx"
                      value={truncate((op.evidence as TransferEvidence).shieldTxId!, 16)}
                    />
                  )}
                  {(op.evidence as TransferEvidence).unshieldTxId && (
                    <Field
                      label="Unshield Tx"
                      value={truncate((op.evidence as TransferEvidence).unshieldTxId!, 16)}
                    />
                  )}
                  {(op.evidence as TransferEvidence).exitSwapId && (
                    <Field
                      label="Exit Swap"
                      value={truncate((op.evidence as TransferEvidence).exitSwapId!, 16)}
                    />
                  )}
                </>
              )}
              {op.type === "enter" && (
                <>
                  {(op.evidence as EnterEvidence).swapId && (
                    <Field
                      label="Swap ID"
                      value={truncate(
                        (op.evidence as EnterEvidence).swapId!,
                        16,
                      )}
                    />
                  )}
                  {(op.evidence as EnterEvidence).shieldTxId && (
                    <Field
                      label="Shield Tx"
                      value={truncate(
                        (op.evidence as EnterEvidence).shieldTxId!,
                        16,
                      )}
                    />
                  )}
                </>
              )}
              {op.type === "exit" && (
                <>
                  {(op.evidence as ExitEvidence).swapId && (
                    <Field
                      label="Swap ID"
                      value={truncate(
                        (op.evidence as ExitEvidence).swapId!,
                        16,
                      )}
                    />
                  )}
                  {(op.evidence as ExitEvidence).unshieldTxId && (
                    <Field
                      label="Unshield Tx"
                      value={truncate(
                        (op.evidence as ExitEvidence).unshieldTxId!,
                        16,
                      )}
                    />
                  )}
                </>
              )}
            </div>

            {op.error && (
              <div className="px-3 py-2.5 rounded-lg bg-blaze/8 border border-blaze/20 text-xs text-blaze font-heading mb-4">
                {op.error}
              </div>
            )}

            {/* Transfer resume: redirect to main app */}
            {resumeAction === "resume_transfer" && (
              <>
                <p className="text-xs text-ash mb-4 leading-relaxed">
                  This transfer can be resumed from the main app page.
                </p>
                <button
                  onClick={() => navigate("/app")}
                  className="w-full py-3 rounded-lg spectral-gradient-bg text-white font-heading text-sm font-semibold hover:shadow-[0_0_25px_rgba(124,58,237,0.25)] transition-all mb-2"
                >
                  Resume Transfer
                </button>
              </>
            )}

            {/* Legacy enter/exit resume action description */}
            {resumeAction !== "none" && resumeAction !== "resume_transfer" && (
              <p className="text-xs text-ash mb-4 leading-relaxed">
                {RESUME_ACTION_DESCRIPTIONS[resumeAction as ResumeAction]}
              </p>
            )}

            {/* Retry broadcast (legacy enter/exit only) */}
            {resumeAction !== "resume_transfer" &&
              (resumeAction === "retry_broadcast_shield" ||
              resumeAction === "retry_broadcast_unshield") && (
              <>
                {broadcastResult && (
                  <div className="px-3 py-2.5 rounded-lg bg-emerald-500/8 border border-emerald-500/15 text-sm text-emerald-400 font-heading mb-3">
                    Broadcast OK: {broadcastResult.slice(0, 20)}...
                  </div>
                )}

                {broadcastError && (
                  <p className="text-sm text-blaze mb-3">{broadcastError}</p>
                )}

                <button
                  onClick={handleRetryBroadcast}
                  disabled={broadcastBusy}
                  className="w-full py-3 rounded-lg spectral-gradient-bg text-white font-heading text-sm font-semibold hover:shadow-[0_0_25px_rgba(124,58,237,0.25)] transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-2"
                >
                  {broadcastBusy
                    ? "Broadcasting..."
                    : RESUME_ACTION_LABELS[resumeAction]}
                </button>
              </>
            )}

            {/* Resume swap tracking */}
            {resumeAction === "resume_exit_swap_tracking" && (
              <>
                {trackingStatus && (
                  <div className="px-3 py-2.5 rounded-lg bg-spectral-violet/8 border border-spectral-violet/15 text-sm text-mist font-heading mb-3">
                    Status: {trackingStatus}
                  </div>
                )}

                <button
                  onClick={handleResumeSwapTracking}
                  disabled={trackingBusy}
                  className="w-full py-3 rounded-lg exit-gradient-bg text-white font-heading text-sm font-semibold hover:shadow-[0_0_25px_rgba(249,115,22,0.2)] transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-2"
                >
                  {trackingBusy
                    ? "Tracking..."
                    : RESUME_ACTION_LABELS[resumeAction]}
                </button>
              </>
            )}

            {/* Dismiss */}
            <button
              onClick={handleDismissOperation}
              disabled={hasChainCommitment(op)}
              className="w-full py-2.5 border border-border rounded-lg text-ash font-heading text-sm hover:border-mist hover:text-mist transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              title={
                hasChainCommitment(op)
                  ? "Cannot dismiss: funds may be at risk. Complete recovery first."
                  : "Dismiss this operation"
              }
            >
              {hasChainCommitment(op)
                ? "Dismiss (locked — recover funds first)"
                : "Dismiss Operation"}
            </button>
          </div>
        )}

        {/* Recover Funds */}
        {acct && (
          <div className="rounded-2xl p-6 mb-4" style={{ background: "#122134" }}>
            {/* Header */}
            <div className="font-heading text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-ash mb-4">
              Recover Funds
            </div>

            {/* Transparent address */}
            <div className="font-mono text-xs text-ash break-all mb-4">
              {acct.transparentAddress}
            </div>

            {/* Orchard scanning */}
            {espectro.orchardScanStatus === "scanning" && (
              <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-spectral-violet/8 border border-spectral-violet/15 mb-4 text-sm text-mist font-heading">
                <div className="w-2 h-2 rounded-full bg-spectral-violet shrink-0 animate-pulse-dot" />
                <span>
                  {espectro.orchardScanProgress?.phase === "downloading"
                    ? `Downloading blocks ${espectro.orchardScanProgress.startHeight}–${espectro.orchardScanProgress.endHeight}...`
                    : espectro.orchardScanProgress?.phase === "scanning"
                    ? `Scanning blocks ${espectro.orchardScanProgress.startHeight}–${espectro.orchardScanProgress.endHeight}...`
                    : "Scanning Orchard pool..."}
                </span>
              </div>
            )}

            {/* Orchard found */}
            {espectro.orchardScanStatus === "found" && espectro.orchardBalance && espectro.recoverStep !== "done" && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-amber-500/8 border border-amber-500/15 text-sm text-amber-400 font-heading mb-4">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 5.5V8.5M8 10.5H8.005M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {espectro.orchardBalance.zec.toFixed(8)} ZEC in Orchard pool
              </div>
            )}

            {/* T1 found (and no orchard, to avoid confusion) */}
            {espectro.orchardScanStatus !== "found" && hasStuckFunds && espectro.recoverStep !== "done" && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-amber-500/8 border border-amber-500/15 text-sm text-amber-400 font-heading mb-4">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 5.5V8.5M8 10.5H8.005M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {transparentTotal.toFixed(8)} ZEC at transparent address
              </div>
            )}

            {/* Nothing found in 3000 blocks */}
            {espectro.orchardScanStatus === "not_found" && !hasStuckFunds && espectro.recoverStep !== "done" && (
              <>
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-500/8 border border-emerald-500/15 text-sm text-emerald-400 font-heading mb-4">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13.5 4.5L6.5 11.5L2.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  No stuck funds in last ~62 hours
                </div>
                <p className="text-xs text-ash mb-3 leading-relaxed">
                  If your transfer is older than 62 hours, paste the shield transaction hash below to scan the exact block.
                </p>
                <input
                  type="text"
                  placeholder="Shield tx hash (ae227d8f...)"
                  value={txHashInput}
                  onChange={(e) => setTxHashInput(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-surface border border-border text-mist font-mono text-xs mb-3 placeholder:text-ash/50"
                />
                <button
                  onClick={() => espectro.scanOrchard(txHashInput.trim() || undefined)}
                  className="w-full py-2.5 border border-border rounded-lg text-ash font-heading text-sm hover:border-mist hover:text-mist transition-all mb-4"
                >
                  Scan with tx hash
                </button>
              </>
            )}

            {/* Scan error */}
            {espectro.orchardScanStatus === "error" && espectro.recoverStep !== "done" && (
              <>
                <p className="text-sm text-blaze mb-3">{espectro.orchardScanError}</p>
                <input
                  type="text"
                  placeholder="Shield tx hash (ae227d8f...)"
                  value={txHashInput}
                  onChange={(e) => setTxHashInput(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-surface border border-border text-mist font-mono text-xs mb-3 placeholder:text-ash/50"
                />
                <button
                  onClick={() => espectro.scanOrchard(txHashInput.trim() || undefined)}
                  className="w-full py-2.5 border border-border rounded-lg text-ash font-heading text-sm hover:border-mist hover:text-mist transition-all mb-4"
                >
                  Retry scan
                </button>
              </>
            )}

            {/* Recovery form — shown when funds detected */}
            {(espectro.orchardScanStatus === "found" || hasStuckFunds) && espectro.recoverStep !== "done" && (
              <>
                <p className="text-xs text-ash mb-3 leading-relaxed">
                  {espectro.orchardScanStatus === "found"
                    ? "Funds will be unshielded to your transparent address, then swapped to your destination."
                    : "Stuck ZEC will be swapped directly to your destination."}
                </p>

                {EXIT_ASSETS.length > 1 && (
                  <select
                    value={recoverSelectedAsset.asset}
                    onChange={(e) => {
                      const found = EXIT_ASSETS.find((a) => a.asset === e.target.value);
                      if (found) setRecoverSelectedAsset(found);
                    }}
                    disabled={!!espectro.recoverStep}
                    className="w-full px-3 py-2.5 rounded-lg bg-surface border border-border text-mist font-heading text-sm mb-3 disabled:opacity-50"
                  >
                    {EXIT_ASSETS.map((a) => (
                      <option key={a.asset} value={a.asset}>{a.label}</option>
                    ))}
                  </select>
                )}

                <input
                  type="text"
                  placeholder={`Destination ${recoverSelectedAsset.label} address`}
                  value={recoverDestAddress}
                  onChange={(e) => setRecoverDestAddress(e.target.value)}
                  disabled={!!espectro.recoverStep}
                  className="w-full px-3 py-2.5 rounded-lg bg-surface border border-border text-mist font-mono text-xs mb-3 placeholder:text-ash/50 disabled:opacity-50"
                />

                {espectro.recoverStep && espectro.recoverStep !== "done" && (() => {
                  const overTime = stepElapsed > (RECOVER_STEP_SECONDS[espectro.recoverStep!] ?? Infinity);
                  return (
                    <div className="px-3 py-2.5 rounded-lg bg-amber-500/8 border border-amber-500/15 mb-3">
                      <div className="flex items-center gap-2.5 text-sm text-amber-400 font-heading">
                        <div className="w-2 h-2 rounded-full bg-amber-500 shrink-0 animate-pulse-dot" />
                        <span className="flex-1">{RECOVER_STEP_LABELS[espectro.recoverStep!] ?? espectro.recoverStep}</span>
                        <span className="text-xs text-amber-400/60 shrink-0">
                          {stepElapsed > 0
                            ? `${stepElapsed < 60 ? `${stepElapsed}s` : `${Math.floor(stepElapsed / 60)}m ${stepElapsed % 60}s`} / ${RECOVER_STEP_TIMES[espectro.recoverStep!] ?? ""}`
                            : (RECOVER_STEP_TIMES[espectro.recoverStep!] ?? "")}
                        </span>
                      </div>
                      {overTime && (
                        <div className="text-xs text-amber-400/70 mt-1.5 ml-4.5">
                          Still processing — your funds are secure
                        </div>
                      )}
                    </div>
                  );
                })()}

                {espectro.recoverError && (
                  <p className="text-sm text-blaze mb-3">{espectro.recoverError}</p>
                )}

                <button
                  onClick={() => espectro.recoverFunds({ destinationAsset: recoverSelectedAsset.asset, destinationAddress: recoverDestAddress })}
                  disabled={!!espectro.recoverStep || !recoverDestAddress.trim()}
                  className="w-full py-3 rounded-lg exit-gradient-bg text-white font-heading text-sm font-semibold hover:shadow-[0_0_25px_rgba(249,115,22,0.2)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {espectro.recoverStep ? "Recovering..." : "Recover Funds"}
                </button>
              </>
            )}

            {/* Success */}
            {espectro.recoverStep === "done" && espectro.recoverResult && (
              <div className="px-3 py-2.5 rounded-lg bg-emerald-500/8 border border-emerald-500/15 text-sm text-emerald-400 font-heading">
                <div className="flex items-center gap-2">
                  <span>Recovery initiated! Tx: {espectro.recoverResult.slice(0, 12)}...{espectro.recoverResult.slice(-6)}</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(espectro.recoverResult!)}
                    className="shrink-0 p-1 rounded hover:bg-emerald-500/10 transition-colors"
                    title="Copy full transaction hash"
                  >
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" className="text-emerald-400/70">
                      <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                      <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2H3.5A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" stroke="currentColor" strokeWidth="1.3" />
                    </svg>
                  </button>
                </div>
                {totalElapsed > 0 && (
                  <div className="text-xs text-emerald-400/60 mt-0.5">
                    Completed in {totalElapsed < 60 ? `${totalElapsed}s` : `${Math.floor(totalElapsed / 60)}m ${totalElapsed % 60}s`}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Info */}
        <div className="rounded-2xl p-6" style={{ background: "#122134" }}>
          <div className="font-heading text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-ash mb-4">
            How Recovery Works
          </div>
          <div className="space-y-3 text-xs text-ash leading-relaxed">
            <p>
              Each transfer is tracked as a durable record that survives
              page refreshes and browser crashes. If a transfer is
              interrupted, it will be detected here with the exact stage and
              available recovery actions.
            </p>
            <p>
              <strong className="text-mist">Retry Broadcast</strong> re-sends
              a transaction that was built but may not have reached the
              network (e.g. browser closed during broadcast).
            </p>
            <p>
              <strong className="text-mist">Return to Origin</strong> swaps
              stuck ZEC back to the original source chain and address via
              SwapKit, without shielding first.
            </p>
            <p>
              <strong className="text-mist">Resume Swap Tracking</strong>{" "}
              re-polls SwapKit for the status of an exit swap that was in
              progress when the page was closed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-border/60 last:border-b-0 gap-3">
      <span className="font-heading text-sm text-mist shrink-0">{label}</span>
      <span className="font-mono text-sm text-ghost-white text-right break-all">
        {value}
      </span>
    </div>
  );
}

function truncate(str: string, chars = 8) {
  if (str.length <= chars * 2 + 3) return str;
  return `${str.slice(0, chars)}...${str.slice(-Math.min(6, chars))}`;
}
